import React, { useMemo, useState } from 'react';
import { Search, Trash2, X } from 'lucide-react';
import { matchesSearch, norm } from '../utils/catalog';
import { TAX_KIND_LABELS, money } from '../utils/quotes';

// Piezas compartidas por el editor de cotizaciones y la venta a mano: selector de cliente, buscador del catálogo y tabla de líneas.
export const inputCls = 'w-full bg-seryi-bg-dark border border-seryi-bg-light rounded-xl px-3 py-2 text-xs text-seryi-heading focus:outline-none focus:border-seryi-green disabled:opacity-60';
export const cellCls = 'w-full bg-seryi-bg-dark border border-seryi-bg-light rounded-lg px-2 py-1.5 text-xs text-seryi-heading focus:outline-none focus:border-seryi-green disabled:opacity-60';
export const labelCls = 'text-[10px] uppercase font-bold text-seryi-text';

export function CustomerPicker({ customers, value, disabled, onPick }) {
  const [q, setQ] = useState('');
  const selected = customers.find((c) => c.id === value);
  const matches = useMemo(() => {
    const terms = norm(q).split(' ').filter(Boolean);
    if (!terms.length) return [];
    return customers.filter((c) => { const hay = norm(`${c.name ?? ''} ${c.legal_name ?? ''} ${c.phone ?? ''} ${c.tax_id ?? ''}`); return terms.every((t) => hay.includes(t)); }).slice(0, 8);
  }, [customers, q]);
  return (
    <div className="space-y-1">
      <span className={labelCls}>Cliente</span>
      {selected ? (
        <div className="flex items-center justify-between gap-2 bg-seryi-bg-dark border border-seryi-bg-light rounded-xl px-3 py-2 text-xs text-seryi-heading">
          <span className="min-w-0">
            <b>{selected.legal_name || selected.name || 'Sin nombre'}</b>
            <span className="block text-[10px] text-seryi-text">{selected.tax_id ? `NIT/CC ${selected.tax_id} · ` : ''}{selected.phone ? `+${selected.phone}` : 'Sin número visible'}</span>
          </span>
          {!disabled && <button type="button" onClick={() => onPick(null)} aria-label="Quitar cliente" className="text-seryi-text hover:text-red-400"><X size={14} /></button>}
        </div>
      ) : (
        <div className="relative">
          <input className={inputCls} placeholder="Buscar cliente por nombre, teléfono o NIT" aria-label="Buscar cliente" disabled={disabled} value={q} onChange={(e) => setQ(e.target.value)} />
          {matches.length > 0 && (
            <ul className="absolute z-20 left-0 right-0 mt-1 bg-seryi-bg border border-seryi-bg-light rounded-xl shadow-xl max-h-56 overflow-auto">
              {matches.map((c) => (
                <li key={c.id}>
                  <button type="button" onClick={() => { onPick(c); setQ(''); }} className="w-full text-left px-3 py-2 text-xs text-seryi-heading hover:bg-seryi-bg-light/40">
                    <b>{c.legal_name || c.name || 'Sin nombre'}</b> <span className="text-seryi-text">{c.phone ? `+${c.phone}` : ''}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
          {q.trim() && matches.length === 0 && <p className="text-[10px] text-seryi-text mt-1">Ningún cliente coincide. Créalo primero en Clientes.</p>}
        </div>
      )}
    </div>
  );
}

export function ProductPicker({ products, priceMap, listKey, defaultKey, onAdd }) {
  const [q, setQ] = useState('');
  const matches = useMemo(() => (q.trim() ? products.filter((p) => p.active && matchesSearch(p, q)).slice(0, 8) : []), [products, q]);
  return (
    <div className="relative">
      <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-seryi-text" />
      <input className={`${inputCls} pl-8`} placeholder="Agregar producto del catálogo (código o nombre)" aria-label="Buscar producto" value={q} onChange={(e) => setQ(e.target.value)} />
      {matches.length > 0 && (
        <ul className="absolute z-20 left-0 right-0 mt-1 bg-seryi-bg border border-seryi-bg-light rounded-xl shadow-xl max-h-64 overflow-auto">
          {matches.map((p) => {
            const own = priceMap.get(p.id) || {};
            const price = own[listKey] ?? own[defaultKey];
            return (
              <li key={p.id}>
                <button type="button" onClick={() => { onAdd(p); setQ(''); }} className="w-full flex items-center justify-between gap-3 text-left px-3 py-2 text-xs text-seryi-heading hover:bg-seryi-bg-light/40">
                  <span><b>{p.sku || '—'}</b> {p.name}</span>
                  <span className="text-emerald-400 font-bold whitespace-nowrap">{price === undefined ? 'sin precio' : money(price)}</span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

// `lines` son las líneas a mostrar (editables o las guardadas). Con editable=false todo queda de solo lectura.
export function LinesTable({ editable, lines, errors = {}, lineTotals = [], onChange, onRemove }) {
  return (
  <div className="overflow-x-auto rounded-3xl border border-seryi-bg-light">
    <table className="w-full text-xs text-seryi-heading min-w-[860px]">
      <thead className="bg-seryi-bg-dark text-seryi-text text-[10px] uppercase">
        <tr><th className="p-2 text-left">Descripción</th><th className="p-2 w-20">Cant.</th><th className="p-2 w-20">Unidad</th><th className="p-2 w-28">Precio</th><th className="p-2 w-16">Desc. %</th><th className="p-2 w-28">Impuesto</th><th className="p-2 w-28 text-right">Total</th><th className="p-2 w-8" /></tr>
      </thead>
      <tbody>
        {lines.map((l, i) => (
          <tr key={l.id} className="border-t border-seryi-bg-light align-top">
            <td className="p-2">
              <input aria-label={`Descripción línea ${i + 1}`} className={cellCls} disabled={!editable} value={l.description} onChange={(e) => onChange(i, { description: e.target.value })} />
              <div className="text-[10px] mt-0.5 space-x-2">
                {l.sku && <span className="text-seryi-text font-mono">{l.sku}</span>}
                {l.noPrice && <span className="text-amber-300">Sin precio en el catálogo: escríbelo.</span>}
                {l.fallbackPrice && <span className="text-amber-300">Precio de la lista predeterminada.</span>}
                {errors[i] && editable && <span className="text-red-400">{errors[i]}</span>}
              </div>
            </td>
            <td className="p-2"><input aria-label={`Cantidad línea ${i + 1}`} type="number" min="0" step="any" className={cellCls} disabled={!editable} value={l.quantity} onChange={(e) => onChange(i, { quantity: e.target.value })} /></td>
            <td className="p-2"><input aria-label={`Unidad línea ${i + 1}`} className={cellCls} disabled={!editable} value={l.unit || ''} onChange={(e) => onChange(i, { unit: e.target.value })} /></td>
            <td className="p-2"><input aria-label={`Precio línea ${i + 1}`} type="number" min="0" step="any" className={cellCls} disabled={!editable} value={l.unit_price} onChange={(e) => onChange(i, { unit_price: e.target.value, noPrice: false })} /></td>
            <td className="p-2"><input aria-label={`Descuento línea ${i + 1}`} type="number" min="0" max="100" step="any" className={cellCls} disabled={!editable} value={l.discount_pct} onChange={(e) => onChange(i, { discount_pct: e.target.value })} /></td>
            <td className="p-2">
              <div className="flex gap-1">
                <select aria-label={`Tipo de impuesto línea ${i + 1}`} className={cellCls} disabled={!editable} value={l.tax_kind} onChange={(e) => onChange(i, { tax_kind: e.target.value, ...(['excluido', 'exento'].includes(e.target.value) ? { tax_rate: 0 } : {}) })}>
                  {Object.entries(TAX_KIND_LABELS).map(([k, label]) => <option key={k} value={k}>{label}</option>)}
                </select>
                <input aria-label={`Tarifa línea ${i + 1}`} type="number" min="0" max="100" step="any" className={`${cellCls} w-14`} disabled={!editable || ['excluido', 'exento'].includes(l.tax_kind)} value={l.tax_rate} onChange={(e) => onChange(i, { tax_rate: e.target.value })} />
              </div>
            </td>
            <td className="p-2 text-right font-bold text-emerald-400">{editable ? (lineTotals[i] === null ? '—' : money(lineTotals[i])) : money(l.line_total)}</td>
            <td className="p-2">{editable && <button type="button" aria-label={`Quitar línea ${i + 1}`} onClick={() => onRemove(i)} className="text-seryi-text hover:text-red-400"><Trash2 size={13} /></button>}</td>
          </tr>
        ))}
        {lines.length === 0 && <tr><td colSpan={8} className="p-6 text-center text-seryi-text">Agrega productos con el buscador de arriba.</td></tr>}
      </tbody>
    </table>
  </div>
  );
}
